/**
 * ExchangePage — Cross-Tenant Document Exchange (DRR-105)
 *
 * Tier 1 implementation:
 * - View active exchange relationships and their approval status
 * - Upload documents via presigned PUT URL (browser → Wasabi direct)
 * - Download documents via presigned GET URL (Wasabi → browser direct)
 * - FERP never touches document bytes — gatekeeper + key master only
 *
 * Route: /exchange (requires exchange:read scope)
 * Guards: Backend RequireScope("exchange:read") on all queries
 */

import { useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  Progress,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  Modal,
  FileInput,
  Textarea,
} from '@mantine/core';
import {
  IconArrowsExchange,
  IconUpload,
  IconDownload,
  IconShieldCheck,
  IconClock,
  IconX,
  IconCheck,
  IconFileText,
  IconAlertTriangle,
  IconBan,
  IconCoins,
  IconHash,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useAuth } from '@/auth/AuthContext';
import {
  SettlementProposalStatus,
  useAcceptSettlementProposalMutation,
  useGetExchangeDocumentsQuery,
  useGetExchangeRelationshipsQuery,
  useInitiateExchangeRelationshipMutation,
  useRecordExchangeConsentMutation,
  useRequestExchangeDownloadUrlMutation,
  useRequestExchangeUploadUrlMutation,
  useRevokeExchangeRelationshipMutation,
  useSubmitSettlementProposalMutation,
} from '@/generated/graphql';

// ---------------------------------------------------------------------------
// GraphQL operations
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Exchange relationship status — mirrors backend ExchangeRelationshipStatus.
 *  TODO: Replace with generated enum once exchange schema is federated. */
const ExchangeRelationshipStatus = {
  Active: 'ACTIVE',
  PendingApproval: 'PENDING_APPROVAL',
  Revoked: 'REVOKED',
  Expired: 'EXPIRED',
} as const;

interface ExchangeRelationship {
  relationshipId: string;
  fromTenantId: string;
  toTenantId: string;
  fromTenantName: string | null;
  toTenantName: string | null;
  status: string;
  approvalCount: number;
  requiredApprovals: number;
  documentCount: number;
  relationshipType: string;
  establishedAt: string | null;
  expiresAt: string | null;
  consentRecords: Array<{
    step: string;
    tenantId: string;
    actorId: string;
    authTier: number;
    witnessVerified: boolean;
    consentedAt: string;
  }>;
}

interface ExchangeDocument {
  documentId: string;
  relationshipId: string;
  senderTenantId: string;
  senderName: string | null;
  senderActorId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  pushedAt: string;
  expiresAt: string;
  downloaded: boolean;
  downloadedAt: string | null;
}

interface SettlementProposalRecord {
  proposalId: string;
  amountCents: number;
  proposerTenantId: string;
  counterpartyTenantId: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  transactionCount: number;
  proposedAt: string;
  proposerReconciliationHash: string;
  counterpartyReconciliationHash?: string | null;
  resolvedAt?: string | null;
  settledAt?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
];

function statusBadge(status: string) {
  switch (status) {
    case ExchangeRelationshipStatus.Active:
      return <Badge color="green" leftSection={<IconCheck size={12} />}>Active</Badge>;
    case ExchangeRelationshipStatus.PendingApproval:
      return <Badge color="yellow" leftSection={<IconClock size={12} />}>Pending Approval</Badge>;
    case ExchangeRelationshipStatus.Revoked:
      return <Badge color="red" leftSection={<IconX size={12} />}>Revoked</Badge>;
    case ExchangeRelationshipStatus.Expired:
      return <Badge color="gray">Expired</Badge>;
    default:
      return <Badge color="gray">{status}</Badge>;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString();
}

// ---------------------------------------------------------------------------
// 6-Step Consent Chain (DRR-105)
// ---------------------------------------------------------------------------

const CONSENT_STEPS = [
  { value: 'A_INITIATES', label: 'Step 1: Initiator Staff', tenant: 'A', tier: 2 },
  { value: 'A_ADMIN_APPROVES', label: 'Step 2: Initiator Admin', tenant: 'A', tier: 3 },
  { value: 'A_OWNER_CONFIRMS', label: 'Step 3: Initiator Owner', tenant: 'A', tier: 4 },
  { value: 'B_REVIEWS', label: 'Step 4: Recipient Staff', tenant: 'B', tier: 2 },
  { value: 'B_ADMIN_APPROVES', label: 'Step 5: Recipient Admin', tenant: 'B', tier: 3 },
  { value: 'B_OWNER_CONFIRMS', label: 'Step 6: Recipient Owner', tenant: 'B', tier: 4 },
] as const;

function getNextConsentStep(
  relationship: ExchangeRelationship,
  tenantId: string,
): typeof CONSENT_STEPS[number] | null {
  const completed = new Set(relationship.consentRecords.map((cr) => cr.step));
  // Find the first step not yet completed that belongs to this tenant's side
  const isInitiator = relationship.fromTenantId === tenantId;
  const tenantSide = isInitiator ? 'A' : 'B';

  for (const step of CONSENT_STEPS) {
    if (!completed.has(step.value) && step.tenant === tenantSide) {
      return step;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Initiate Exchange Modal (DRR-105 Tier 2)
// ---------------------------------------------------------------------------

interface InitiateExchangeModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly tenantId: string;
  readonly onSuccess: () => void;
}

function InitiateExchangeModal({ opened, onClose, tenantId, onSuccess }: Readonly<InitiateExchangeModalProps>) {
  const [relationshipId, setRelationshipId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [initiateExchange] = useInitiateExchangeRelationshipMutation();

  const handleSubmit = useCallback(async () => {
    if (!relationshipId.trim() || !purpose.trim()) {
      setError('Relationship ID and purpose are required.');
      return;
    }
    setInitiating(true);
    setError(null);
    try {
      await initiateExchange({
        variables: {
          input: {
            relationshipId: relationshipId.trim(),
            purpose: purpose.trim(),
          },
        },
      });
      setRelationshipId('');
      setPurpose('');
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate exchange.');
    } finally {
      setInitiating(false);
    }
  }, [relationshipId, purpose, initiateExchange, onSuccess, onClose]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Initiate Exchange Relationship"
      size="md"
    >
      <Stack gap="md">
        <Alert color="blue" variant="light" icon={<IconShieldCheck size={16} />}>
          Exchange relationships require 6-step chain-of-command approval
          (3 steps per tenant). This initiates Step 1.
        </Alert>

        <TextInput
          label="Relationship ID"
          placeholder="e.g., rel-011"
          description="Identifies the pre-configured relationship type"
          value={relationshipId}
          onChange={(e) => setRelationshipId(e.currentTarget.value)}
          required
        />

        <Textarea
          label="Purpose"
          placeholder="Describe the business reason for this exchange..."
          description="Recorded in the audit trail for compliance review"
          value={purpose}
          onChange={(e) => setPurpose(e.currentTarget.value)}
          minRows={3}
          required
        />

        {error && <Alert color="red">{error}</Alert>}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={initiating}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={initiating}
            leftSection={<IconArrowsExchange size={16} />}
          >
            Initiate (Step 1 of 6)
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Upload Modal
// ---------------------------------------------------------------------------

interface UploadModalProps {
  opened: boolean;
  onClose: () => void;
  relationship: ExchangeRelationship | null;
  tenantId: string;
}

function UploadModal({ opened, onClose, relationship, tenantId }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [piiWarning, setPiiWarning] = useState<string[] | null>(null);

  const [requestUploadUrl] = useRequestExchangeUploadUrlMutation();

  const handleUpload = useCallback(async () => {
    if (!file || !relationship) return;

    // DRR-081 allowlist check (also enforced by backend)
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setUploadError(`File type "${file.type}" not allowed. Only PDF, TXT, CSV, and MD are permitted.`);
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const { data } = await requestUploadUrl({
        variables: {
          input: {
            relationshipId: relationship.relationshipId,
            fileName: file.name,
            fileType: file.type,
            fileSizeBytes: file.size,
          },
        },
      });

      const { uploadUrl, piiEntitiesDetected } = (data as unknown as { requestExchangeUploadUrl: { uploadUrl: string; piiEntitiesDetected: string[] | null } }).requestExchangeUploadUrl;

      // DRR-064: Warn if PII detected in metadata (filename)
      if (piiEntitiesDetected && piiEntitiesDetected.length > 0) {
        setPiiWarning(piiEntitiesDetected);
      }

      // Direct browser → Wasabi upload via presigned PUT URL
      // FERP never touches the bytes
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      setUploadSuccess(true);
      setFile(null);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [file, relationship, requestUploadUrl]);

  const handleClose = useCallback(() => {
    setFile(null);
    setUploadError(null);
    setUploadSuccess(false);
    setPiiWarning(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Push Document to Exchange"
      size="md"
    >
      <Stack gap="md">
        {relationship && (
          <Text size="sm" c="dimmed">
            Sending to: {relationship.toTenantName ?? relationship.toTenantId}
          </Text>
        )}

        <FileInput
          label="Document"
          placeholder="Select file (PDF, TXT, CSV, MD only)"
          accept=".pdf,.txt,.csv,.md"
          value={file}
          onChange={setFile}
          leftSection={<IconFileText size={16} />}
        />

        {uploadError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {uploadError}
          </Alert>
        )}

        {piiWarning && piiWarning.length > 0 && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="PII Detected in Filename">
            The following PII entity types were detected in the document filename:
            {' '}{piiWarning.join(', ')}.
            Consider renaming the file to remove personally identifiable information
            before sharing across tenants. This has been logged for compliance review.
          </Alert>
        )}

        {uploadSuccess && (
          <Alert color="green" icon={<IconCheck size={16} />}>
            Document pushed successfully. The recipient can now download it.
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleUpload}
            loading={uploading}
            disabled={!file || uploadSuccess}
            leftSection={<IconUpload size={16} />}
          >
            Push Document
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Revoke Modal (DRR-105)
// ---------------------------------------------------------------------------

interface RevokeModalProps {
  opened: boolean;
  onClose: () => void;
  relationship: ExchangeRelationship | null;
  onSuccess: () => void;
}

function RevokeModal({ opened, onClose, relationship, onSuccess }: RevokeModalProps) {
  const [reason, setReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [revokeExchange] = useRevokeExchangeRelationshipMutation();

  const handleRevoke = useCallback(async () => {
    if (!relationship) return;
    if (!reason.trim()) {
      setRevokeError('A reason is required for the audit trail.');
      return;
    }

    setRevoking(true);
    setRevokeError(null);

    try {
      await revokeExchange({
        variables: {
          input: {
            relationshipId: relationship.relationshipId,
            reason: reason.trim(),
          },
        },
      });
      setReason('');
      onSuccess();
      onClose();
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Failed to revoke exchange.');
    } finally {
      setRevoking(false);
    }
  }, [relationship, reason, revokeExchange, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    setReason('');
    setRevokeError(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Revoke Exchange Relationship"
      size="md"
    >
      <Stack gap="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          This will permanently disable the encryption key. All exchanged documents
          will become unreadable. This action CANNOT be undone.
        </Alert>

        {relationship && (
          <Text size="sm">
            Revoking exchange between{' '}
            <Text span fw={600}>{relationship.fromTenantName ?? relationship.fromTenantId}</Text>
            {' and '}
            <Text span fw={600}>{relationship.toTenantName ?? relationship.toTenantId}</Text>
          </Text>
        )}

        <Textarea
          label="Reason for revocation"
          placeholder="Describe why this exchange is being revoked..."
          description="Required for compliance audit trail"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
          required
        />

        <Badge color="red" size="lg" leftSection={<IconAlertTriangle size={14} />}>
          Requires Tier 4 + Witness
        </Badge>

        {revokeError && <Alert color="red">{revokeError}</Alert>}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose} disabled={revoking}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleRevoke}
            loading={revoking}
            disabled={!reason.trim()}
            leftSection={<IconBan size={16} />}
          >
            Revoke Exchange
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Document Table
// ---------------------------------------------------------------------------

interface DocumentTableProps {
  relationshipId: string;
}

function DocumentTable({ relationshipId }: DocumentTableProps) {
  const { data, loading, error } = useGetExchangeDocumentsQuery({
    variables: { relationshipId },
    fetchPolicy: 'network-only',
  });

  const [requestDownloadUrl] = useRequestExchangeDownloadUrlMutation();

  const handleDownload = useCallback(async (doc: ExchangeDocument) => {
    try {
      const { data: urlData } = await requestDownloadUrl({
        variables: {
          input: {
            relationshipId: doc.relationshipId,
            documentId: doc.documentId,
          },
        },
      });
      // Direct Wasabi → browser download via presigned GET URL
      window.open((urlData as { requestExchangeDownloadUrl: { downloadUrl: string } }).requestExchangeDownloadUrl.downloadUrl, '_blank');
    } catch (err) {
      // Error is shown via Apollo error handling
      console.error('Download URL request failed:', err);
    }
  }, [requestDownloadUrl]);

  if (loading) return <Center p="md"><Loader size="sm" /></Center>;
  if (error) return <Alert color="red">{error.message}</Alert>;

  const docs: ExchangeDocument[] = (data as { exchangeDocuments?: ExchangeDocument[] } | undefined)?.exchangeDocuments ?? [];

  if (docs.length === 0) {
    return <Text size="sm" c="dimmed" ta="center" p="md">No documents exchanged yet.</Text>;
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Filename</Table.Th>
          <Table.Th>Type</Table.Th>
          <Table.Th>Size</Table.Th>
          <Table.Th>Sender</Table.Th>
          <Table.Th>Pushed</Table.Th>
          <Table.Th>Downloaded</Table.Th>
          <Table.Th>Expires</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {docs.map((doc) => (
          <Table.Tr key={doc.documentId}>
            <Table.Td>
              <Group gap="xs">
                <IconFileText size={16} />
                <Text size="sm">{doc.fileName}</Text>
              </Group>
            </Table.Td>
            <Table.Td><Text size="sm">{doc.fileType}</Text></Table.Td>
            <Table.Td><Text size="sm">{formatBytes(doc.fileSizeBytes)}</Text></Table.Td>
            <Table.Td><Text size="sm">{doc.senderName ?? doc.senderActorId}</Text></Table.Td>
            <Table.Td><Text size="sm">{formatDate(doc.pushedAt)}</Text></Table.Td>
            <Table.Td>
              {doc.downloaded
                ? <Text size="sm">{formatDate(doc.downloadedAt)}</Text>
                : <Badge size="sm" color="blue">Not yet</Badge>}
            </Table.Td>
            <Table.Td><Text size="sm">{formatDate(doc.expiresAt)}</Text></Table.Td>
            <Table.Td>
              <Tooltip label="Download via presigned URL">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => handleDownload(doc)}
                >
                  Download
                </Button>
              </Tooltip>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function settlementStatusBadge(status: string) {
  switch (status) {
    case SettlementProposalStatus.PROPOSED:
      return <Badge color="yellow">Proposed</Badge>;
    case SettlementProposalStatus.ACCEPTED:
      return <Badge color="blue">Accepted</Badge>;
    case SettlementProposalStatus.SETTLING:
      return <Badge color="indigo">Settling</Badge>;
    case SettlementProposalStatus.SETTLED:
      return <Badge color="green" leftSection={<IconCheck size={12} />}>Settled</Badge>;
    case SettlementProposalStatus.REJECTED:
      return <Badge color="red" leftSection={<IconX size={12} />}>Rejected</Badge>;
    case SettlementProposalStatus.FAILED:
      return <Badge color="red" leftSection={<IconAlertTriangle size={12} />}>Failed</Badge>;
    default:
      return <Badge color="gray">{status}</Badge>;
  }
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

// ---------------------------------------------------------------------------
// Settlement Modals (DRR-109)
// ---------------------------------------------------------------------------

interface SubmitSettlementModalProps {
  opened: boolean;
  onClose: () => void;
  relationships: ExchangeRelationship[];
  onSubmitted: (proposal: SettlementProposalRecord) => void;
}

function SubmitSettlementModal({ opened, onClose, relationships, onSubmitted }: SubmitSettlementModalProps) {
  const [counterpartyTenantId, setCounterpartyTenantId] = useState('');
  const [amountCents, setAmountCents] = useState<number | string>('');
  const [transactionCount, setTransactionCount] = useState<number | string>('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [reconciliationHash, setReconciliationHash] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [submitProposal, { loading }] = useSubmitSettlementProposalMutation();

  const activeRelIds = relationships
    .filter((r) => r.status === ExchangeRelationshipStatus.Active)
    .map((r) => r.toTenantId);

  // Find relationship ID for the selected counterparty
  const selectedRel = relationships.find(
    (r) => r.status === ExchangeRelationshipStatus.Active && r.toTenantId === counterpartyTenantId
  );

  const handleSubmit = async () => {
    if (!counterpartyTenantId || !amountCents || !transactionCount || !periodStart || !periodEnd || !reconciliationHash) {
      setError('All fields required');
      return;
    }
    setError(null);
    try {
      const result = await submitProposal({
        variables: {
          input: {
            counterpartyTenantId,
            amountCents: Number(amountCents),
            transactionCount: Number(transactionCount),
            periodStart,
            periodEnd,
            reconciliationHash,
            relationshipId: selectedRel?.relationshipId ?? '',
          },
        },
      });
      const submitData = result.data as { submitSettlementProposal: SettlementProposalRecord };
      onSubmitted(submitData.submitSettlementProposal);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Submit failed');
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Submit Settlement Proposal (DRR-109)" size="lg">
      <Stack gap="md">
        <Alert color="blue" variant="light">
          Settlement proposals are immutable records. Requires <strong>exchange:admin</strong> scope + Tier 3 auth.
          Blake3 hash must match your aggregated transaction data.
        </Alert>

        {activeRelIds.length === 0 && (
          <Alert color="orange">No active exchange relationships. Establish a relationship first.</Alert>
        )}

        <TextInput
          label="Counterparty Tenant"
          placeholder="Select from active relationships"
          value={counterpartyTenantId}
          onChange={(e) => setCounterpartyTenantId(e.target.value)}
          list="counterparty-tenants"
        />
        <datalist id="counterparty-tenants">
          {relationships
            .filter((r) => r.status === ExchangeRelationshipStatus.Active)
            .map((r) => (
              <option key={r.relationshipId} value={r.toTenantId}>
                {r.toTenantName ?? r.toTenantId}
              </option>
            ))}
        </datalist>

        <Group grow>
          <NumberInput
            label="Amount (cents USD)"
            description="e.g. 150000 = $1,500.00"
            value={amountCents}
            onChange={setAmountCents}
            min={1}
          />
          <NumberInput
            label="Transaction Count"
            description="Transactions aggregated into this settlement"
            value={transactionCount}
            onChange={setTransactionCount}
            min={1}
          />
        </Group>

        <Group grow>
          <TextInput
            label="Period Start"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
          <TextInput
            label="Period End"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </Group>

        <TextInput
          label="Blake3 Reconciliation Hash"
          description="Hash of your aggregated transaction data — must match counterparty's hash"
          placeholder="64-char hex"
          leftSection={<IconHash size={16} />}
          value={reconciliationHash}
          onChange={(e) => setReconciliationHash(e.target.value)}
          styles={{ input: { fontFamily: 'monospace' } }}
        />

        {error && <Alert color="red">{error}</Alert>}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            leftSection={<IconCoins size={16} />}
            loading={loading}
            onClick={handleSubmit}
            disabled={activeRelIds.length === 0}
          >
            Submit Proposal
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface AcceptSettlementModalProps {
  opened: boolean;
  onClose: () => void;
  proposal: SettlementProposalRecord | null;
  onAccepted: (updated: SettlementProposalRecord) => void;
}

function AcceptSettlementModal({ opened, onClose, proposal, onAccepted }: AcceptSettlementModalProps) {
  const [reconciliationHash, setReconciliationHash] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [accept, { loading }] = useAcceptSettlementProposalMutation();

  if (!proposal) return null;

  const handleAccept = async () => {
    if (!reconciliationHash) { setError('Blake3 hash required'); return; }
    setError(null);
    try {
      const result = await accept({
        variables: { input: { proposalId: proposal.proposalId, reconciliationHash } },
      });
      const acceptData = result.data as { acceptSettlementProposal: SettlementProposalRecord };
      onAccepted(acceptData.acceptSettlementProposal);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Accept failed');
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Accept Settlement Proposal" size="md">
      <Stack gap="md">
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          Acceptance is immutable. Requires <strong>exchange:admin</strong> scope + Tier 3 auth.
          Your Blake3 hash will be permanently recorded against the proposal.
        </Alert>

        <Card withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Proposal ID</Text>
              <Text size="sm" ff="monospace">{proposal.proposalId}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Amount</Text>
              <Text size="sm" fw={600}>{formatCents(proposal.amountCents)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Transactions</Text>
              <Text size="sm">{proposal.transactionCount}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Period</Text>
              <Text size="sm">{proposal.periodStart} → {proposal.periodEnd}</Text>
            </Group>
            <Divider />
            <Text size="xs" c="dimmed">Proposer hash:</Text>
            <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
              {proposal.proposerReconciliationHash}
            </Text>
          </Stack>
        </Card>

        <TextInput
          label="Your Blake3 Reconciliation Hash"
          description="Your independently-computed hash of the aggregated transaction data"
          placeholder="64-char hex"
          leftSection={<IconHash size={16} />}
          value={reconciliationHash}
          onChange={(e) => setReconciliationHash(e.target.value)}
          styles={{ input: { fontFamily: 'monospace' } }}
        />

        {error && <Alert color="red">{error}</Alert>}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button color="green" leftSection={<IconCheck size={16} />} loading={loading} onClick={handleAccept}>
            Accept Proposal
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Settlements Section (DRR-109)
// ---------------------------------------------------------------------------

interface SettlementsSectionProps {
  relationships: ExchangeRelationship[];
}

function SettlementsSection({ relationships }: SettlementsSectionProps) {
  const [proposals, setProposals] = useState<SettlementProposalRecord[]>([]);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState<SettlementProposalRecord | null>(null);

  const handleSubmitted = (proposal: SettlementProposalRecord) => {
    setProposals((prev) => [proposal, ...prev]);
  };

  const handleAccepted = (updated: SettlementProposalRecord) => {
    setProposals((prev) => prev.map((p) => p.proposalId === updated.proposalId ? { ...p, ...updated } : p));
  };

  const proposedProposals = proposals.filter((p) => p.status === SettlementProposalStatus.PROPOSED);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Stack gap={0}>
          <Text fw={600}>Settlement Proposals</Text>
          <Text size="sm" c="dimmed">
            DRR-109: Blake3-reconciled financial settlements between tenants.
            Proposals are immutable — created once, accepted or rejected.
          </Text>
        </Stack>
        <Button leftSection={<IconCoins size={16} />} onClick={() => setSubmitOpen(true)}>
          Submit Proposal
        </Button>
      </Group>

      {proposals.length === 0 ? (
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="sm">
              <IconCoins size={40} color="gray" />
              <Text size="sm" c="dimmed" ta="center">
                No settlement proposals this session. Submit a proposal to initiate a Blake3-reconciled settlement.
              </Text>
            </Stack>
          </Center>
        </Paper>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Proposal ID</Table.Th>
              <Table.Th>Counterparty</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>Period</Table.Th>
              <Table.Th>Tx Count</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Proposed</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {proposals.map((p) => (
              <Table.Tr key={p.proposalId}>
                <Table.Td>
                  <Text size="xs" ff="monospace">{p.proposalId.substring(0, 12)}…</Text>
                </Table.Td>
                <Table.Td>{p.counterpartyTenantId}</Table.Td>
                <Table.Td fw={600}>{formatCents(p.amountCents)}</Table.Td>
                <Table.Td>
                  <Text size="xs">{p.periodStart} → {p.periodEnd}</Text>
                </Table.Td>
                <Table.Td>{p.transactionCount}</Table.Td>
                <Table.Td>{settlementStatusBadge(p.status)}</Table.Td>
                <Table.Td>
                  <Text size="xs">{formatDate(p.proposedAt)}</Text>
                </Table.Td>
                <Table.Td>
                  {p.status === SettlementProposalStatus.PROPOSED && (
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      leftSection={<IconCheck size={14} />}
                      onClick={() => setAcceptTarget(p)}
                    >
                      Accept
                    </Button>
                  )}
                  {p.counterpartyReconciliationHash && (
                    <Tooltip label={`Counterparty hash: ${p.counterpartyReconciliationHash}`}>
                      <Badge color="teal" size="sm" ml="xs">Hash ✓</Badge>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {proposedProposals.length > 0 && (
        <Alert color="blue" variant="light">
          {proposedProposals.length} proposal(s) awaiting counterparty acceptance.
          Share your Blake3 hash with the counterparty to complete reconciliation.
        </Alert>
      )}

      <SubmitSettlementModal
        opened={submitOpen}
        onClose={() => setSubmitOpen(false)}
        relationships={relationships}
        onSubmitted={handleSubmitted}
      />
      <AcceptSettlementModal
        opened={acceptTarget !== null}
        onClose={() => setAcceptTarget(null)}
        proposal={acceptTarget}
        onAccepted={handleAccepted}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function ExchangePage() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const [selectedRelationship, setSelectedRelationship] = useState<ExchangeRelationship | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [initiateModalOpen, setInitiateModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [revokeRelationship, setRevokeRelationship] = useState<ExchangeRelationship | null>(null);
  const [expandedRelationship, setExpandedRelationship] = useState<string | null>(null);
  const [consentLoading, setConsentLoading] = useState<string | null>(null);

  const { data, loading, error, refetch } = useGetExchangeRelationshipsQuery({
    fetchPolicy: 'network-only',
  });

  const [recordConsent] = useRecordExchangeConsentMutation();

  const relationships: ExchangeRelationship[] = (data as { exchangeRelationships?: ExchangeRelationship[] } | undefined)?.exchangeRelationships ?? [];
  const activeRelationships = relationships.filter((r) => r.status === ExchangeRelationshipStatus.Active);
  const pendingRelationships = relationships.filter((r) => r.status === ExchangeRelationshipStatus.PendingApproval);

  const handleUploadClick = useCallback((rel: ExchangeRelationship) => {
    setSelectedRelationship(rel);
    setUploadModalOpen(true);
  }, []);

  const handleRevokeClick = useCallback((rel: ExchangeRelationship) => {
    setRevokeRelationship(rel);
    setRevokeModalOpen(true);
  }, []);

  const toggleExpand = useCallback((relId: string) => {
    setExpandedRelationship((prev) => (prev === relId ? null : relId));
  }, []);

  /** Approve the next consent step for a pending relationship */
  const handleApproveStep = useCallback(async (rel: ExchangeRelationship) => {
    if (!tenantId) return;
    const nextStep = getNextConsentStep(rel, tenantId);
    if (!nextStep) return;

    setConsentLoading(rel.relationshipId);
    try {
      await recordConsent({
        variables: {
          input: {
            relationshipId: rel.relationshipId,
            step: nextStep.value,
          },
        },
      });
      refetch();
    } catch (err) {
      console.error('Consent recording failed:', err);
    } finally {
      setConsentLoading(null);
    }
  }, [tenantId, recordConsent, refetch]);

  if (!tenantId) {
    return <Alert color="yellow">No tenant context. Please select a tenant.</Alert>;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading exchange relationships">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="sm">
          <IconArrowsExchange size={28} />
          <Title order={3}>Cross-Tenant Exchange</Title>
        </Group>
        <Group gap="xs">
          <Badge color="green" size="lg">{activeRelationships.length} Active</Badge>
          <Badge color="yellow" size="lg">{pendingRelationships.length} Pending</Badge>
          <Button
            leftSection={<IconArrowsExchange size={16} />}
            onClick={() => setInitiateModalOpen(true)}
          >
            Initiate Exchange
          </Button>
        </Group>
      </Group>

      <Text size="sm" c="dimmed">
        Secure document exchange between tenants. Documents are transferred via presigned URLs —
        FERP manages keys and metadata but never touches document bytes.
      </Text>

      {/* Loading */}
      {loading && (
        <Center p="xl"><Loader /></Center>
      )}

      {/* No relationships */}
      {!loading && relationships.length === 0 && (
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="sm">
              <IconShieldCheck size={48} color="gray" />
              <Text size="lg" fw={500}>No Exchange Relationships</Text>
              <Text size="sm" c="dimmed" ta="center">
                Exchange relationships require 6-step chain-of-command approval
                (3 per tenant). Contact your administrator to initiate one.
              </Text>
            </Stack>
          </Center>
        </Paper>
      )}

      {/* Relationship cards */}
      {relationships.map((rel) => (
        <Card key={rel.relationshipId} withBorder shadow="sm" padding="lg">
          <Stack gap="md">
            {/* Relationship header */}
            <Group justify="space-between">
              <Group gap="md">
                <Text fw={600}>
                  {rel.fromTenantName ?? rel.fromTenantId}
                </Text>
                <IconArrowsExchange size={20} color="gray" />
                <Text fw={600}>
                  {rel.toTenantName ?? rel.toTenantId}
                </Text>
              </Group>
              {statusBadge(rel.status)}
            </Group>

            {/* Approval progress */}
            {rel.status === ExchangeRelationshipStatus.PendingApproval && (
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">
                    Approval Progress: {rel.approvalCount} / {rel.requiredApprovals}
                  </Text>
                  <Text size="xs" c="dimmed">
                    6-step chain-of-command (wf-4-0-053)
                  </Text>
                </Group>
                <Progress
                  value={(rel.approvalCount / rel.requiredApprovals) * 100}
                  color="blue"
                  size="lg"
                  striped
                  animated
                />
                {/* Consent steps */}
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Step</Table.Th>
                      <Table.Th>Actor</Table.Th>
                      <Table.Th>Tenant</Table.Th>
                      <Table.Th>Auth Tier</Table.Th>
                      <Table.Th>Witness</Table.Th>
                      <Table.Th>Consented</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rel.consentRecords.map((cr) => (
                      <Table.Tr key={cr.step}>
                        <Table.Td><Badge size="sm">{cr.step}</Badge></Table.Td>
                        <Table.Td>{cr.actorId}</Table.Td>
                        <Table.Td>{cr.tenantId}</Table.Td>
                        <Table.Td>
                          <Badge color={cr.authTier >= 4 ? 'red' : 'blue'} size="sm">
                            Tier {cr.authTier}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {cr.witnessVerified
                            ? <Badge color="green" size="sm">Verified</Badge>
                            : <Badge color="gray" size="sm">N/A</Badge>}
                        </Table.Td>
                        <Table.Td>{formatDate(cr.consentedAt)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                {/* Approve next step action (DRR-105 Tier 2) */}
                {tenantId && (() => {
                  const nextStep = getNextConsentStep(rel, tenantId);
                  if (!nextStep) return null;
                  return (
                    <Group gap="sm" mt="xs">
                      <Button
                        color="green"
                        leftSection={<IconCheck size={16} />}
                        loading={consentLoading === rel.relationshipId}
                        onClick={() => handleApproveStep(rel)}
                      >
                        Approve: {nextStep.label}
                      </Button>
                      {nextStep.tier >= 4 && (
                        <Badge color="red" size="sm" leftSection={<IconAlertTriangle size={12} />}>
                          Requires Tier 4 + Witness
                        </Badge>
                      )}
                    </Group>
                  );
                })()}
              </Stack>
            )}

            {/* Timestamps */}
            <Group gap="lg">
              {rel.establishedAt && <Text size="xs" c="dimmed">Established: {formatDate(rel.establishedAt)}</Text>}
              {rel.expiresAt && <Text size="xs" c="dimmed">Expires: {formatDate(rel.expiresAt)}</Text>}
              {rel.documentCount > 0 && <Text size="xs" c="dimmed">Documents: {rel.documentCount}</Text>}
            </Group>

            {/* Actions for active relationships */}
            {rel.status === ExchangeRelationshipStatus.Active && (
              <Group gap="sm">
                <Button
                  variant="light"
                  leftSection={<IconUpload size={16} />}
                  onClick={() => handleUploadClick(rel)}
                >
                  Push Document
                </Button>
                <Button
                  variant="subtle"
                  leftSection={<IconFileText size={16} />}
                  onClick={() => toggleExpand(rel.relationshipId)}
                >
                  {expandedRelationship === rel.relationshipId ? 'Hide' : 'View'} Documents
                </Button>
                <Button
                  variant="subtle"
                  color="red"
                  leftSection={<IconBan size={16} />}
                  onClick={() => handleRevokeClick(rel)}
                >
                  Revoke
                </Button>
              </Group>
            )}

            {/* Expanded document table */}
            {expandedRelationship === rel.relationshipId && (
              <DocumentTable
                relationshipId={rel.relationshipId}
              />
            )}
          </Stack>
        </Card>
      ))}

      {/* Upload modal */}
      <UploadModal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        relationship={selectedRelationship}
        tenantId={tenantId ?? ''}
      />

      {/* Initiate Exchange modal (DRR-105 Tier 2) */}
      <InitiateExchangeModal
        opened={initiateModalOpen}
        onClose={() => setInitiateModalOpen(false)}
        tenantId={tenantId}
        onSuccess={() => refetch()}
      />

      {/* Revoke Exchange modal (DRR-105) */}
      <RevokeModal
        opened={revokeModalOpen}
        onClose={() => setRevokeModalOpen(false)}
        relationship={revokeRelationship}
        onSuccess={() => refetch()}
      />

      {/* Settlements (DRR-109) */}
      <Divider label="Settlements (DRR-109)" labelPosition="left" mt="md" />
      <SettlementsSection relationships={relationships} />
    </Stack>
  );
}

export default ExchangePage;
